import { AssetAccess } from './AssetAccess';
import { ImageViewer } from '../player/ImageViewer';
import { GraphicAsset } from '../components/GraphicAsset';
import { EditGraphicButton } from '../graphics/EditGraphicButton';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PaneControls, PaneGroup, PanesMenu, usePaneLayout } from '../components/Panes';
import { OverflowTabs } from '../components/OverflowTabs';
import { OverflowBar } from '../components/OverflowBar';
import { AlertOctagon, AlertTriangle, ArrowLeft, CircleDashed, Download, FileQuestion, Upload, Loader2, RotateCcw, Server } from 'lucide-react';
import { applyCueStyle, parseTimecode, programmeMarks, type AssetDetail, type ProcessingRunDTO } from '@prism/contracts';
import { ApiError, errorMessage } from '../api/client';
import { useAsset, useAssetAction, useMe, useSaveMetadata, useSchema, useSubtitles } from '../api/hooks';
import { Player, type PlayerHandle } from '../player/Player';
import { LivePlayer } from '../player/LivePlayer';
import { SubtitlesTab, type SubtitlePlayer } from './SubtitlesTab';
import {
  assetRate, clickSelection, discardDraft, editDraft, getCueSelection, getDraft, hasUnsavedDrafts, reconcileDraft, selectedTrack, setCueSelection, setDraft, useDraftStore,
  type SubtitleEditing,
} from '../subtitles';
import { AttachmentsTab } from './AttachmentsTab';
import { SharingTab } from './SharingTab';
import { ShareButton } from '../components/ShareAsset';
import { LogTab } from './LogTab';
import { CategoryPicker, RelationsTab } from './RelationsTab';
// 1.8.0: the Rights tab and the header pill (only while rights management is on: the asset carries `rights`).
import { RightsTab } from './RightsTab';
import { RightsPill } from '../components/RightsMark';
import { DeleteAssetButton, DeletedBanner } from '../components/DeleteAsset';
import { RunWorkflowButton } from '../components/RunWorkflow';
import { AddToCollectionButton } from '../components/AddToCollection';
import { AssetExportButton } from '../components/AssetExport';
import { AssignButton } from '../components/AssignTask';
import { AssetStatus } from '../components/Status';
import { RestoreFromArchive } from '../components/ArchiveRestore';
import { AssetCode } from '../components/AssetCode';
import { uploader, useUploads } from '../upload/uploader';
import { uploadAccept } from '../lib/upload-accept';
import { toast } from '../components/Toasts';
import { usePref } from '../lib/prefs';
import { isTyping } from '../lib/keys';
import { EmptyState } from '../components/EmptyState';
import { MetadataTab } from './MetadataTab';
import { TechnicalTab } from './TechnicalTab';
import { StorageTab } from './StorageTab';
import { ProcessingTab, RetryBar, Steps } from './ProcessingTab';
import { ActivityTab } from './ActivityTab';
import { QcTab } from './QcTab';
import { QcBadge, RunQcButton } from '../components/QualityControl';
import { DetectShotsButton } from '../components/DetectShotsDialog';
import { RegenerateProxyButton } from '../components/RegenerateProxy';

import { MastersTab } from './MastersTab';
import { AssetUploadButton } from '../components/AssetUpload';
import { VersionsTab } from './VersionsTab';
import { AssignForDeliveryButton, PlaceholderDelivery } from './assignments/AssignForDelivery';
import { AssetGroupsBreadcrumb } from './groups/AssetGroups';

/** 1.9.0: what leaving an asset with unsaved subtitle edits asks. */
const LEAVE_SUBTITLES = 'You have unsaved subtitle changes on this asset. Leave and lose them?';

type Tab = 'metadata' | 'rights' | 'related' | 'log' | 'subtitles' | 'attachments' | 'sharing' | 'technical' | 'masters' | 'versions' | 'qc' | 'storage' | 'processing' | 'activity';

export function AssetPage() {
  const { id } = useParams<{ id: string }>();
  const q = useAsset(id);
  const navigate = useNavigate();
  const [tab, setTab] = usePref<Tab>('asset.tab', 'metadata');
  const playerRef = useRef<PlayerHandle>(null);
  const [playerTime, setPlayerTime] = useState(0);
  // 1.9.3: the Log and Subtitles tabs' Follow ends a pause made by scrolling by hand on the next play.
  const [playerPlaying, setPlayerPlaying] = useState(false);
  const subtitles = useSubtitles(id);
  const schema = useSchema();
  const me = useMe();
  const saveMetadata = useSaveMetadata(id ?? '');
  // Pane contents are drawn once the asset is loaded (they are set below, after the early returns).
  const paneRender = useRef<{ player: () => React.ReactNode; details: () => React.ReactNode }>({ player: () => null, details: () => null });
  const panes = usePaneLayout('asset', {
    main: [
      { id: 'player', label: 'Player', required: true, defaultSize: '64%', minSize: '420px', render: () => paneRender.current.player() },
      { id: 'details', label: 'Details', defaultSize: '36%', minSize: '320px', render: () => paneRender.current.details() },
    ],
  });
  // 1.9.0: the subtitle drafts (subtitles/draftStore.ts) follow the server's tracks: adopted while clean, a newer
  // revision noted while dirty; drafts of tracks that are gone are dropped.
  useDraftStore();
  const assetId = q.data?.id;
  useEffect(() => {
    const items = subtitles.data?.items;
    if (!assetId || !items) return;
    for (const t of items) setDraft(reconcileDraft(getDraft(assetId, t.id), assetId, t));
  }, [assetId, subtitles.data]);
  const rate = assetRate(q.data?.timing, q.data?.frameRate);
  const startTc = q.data?.timing.startTimecode;
  const startFrames = useMemo(() => (startTc ? parseTimecode(startTc, rate) ?? 0 : 0), [startTc, rate.num, rate.den]); // eslint-disable-line react-hooks/exhaustive-deps
  const subtitlePlayer: SubtitlePlayer = useMemo(() => ({
    now: () => playerRef.current?.element()?.currentTime ?? 0,
    seek: (sec) => playerRef.current?.seek(sec),
    playRange: (a, b) => playerRef.current?.playRange(a, b),
  }), []);
  // Leaving the asset with unsaved subtitle edits asks first (the GraphicsEditor pattern: closing or reloading the
  // page, links in PRISM, Back); leaving anyway drops the drafts.
  const unsaved = assetId ? hasUnsavedDrafts(assetId) : false;
  const unsavedRef = useRef(unsaved);
  unsavedRef.current = unsaved;
  const dropDrafts = () => { if (assetId) for (const t of subtitles.data?.items ?? []) discardDraft(assetId, t.id); };
  const dropRef = useRef(dropDrafts);
  dropRef.current = dropDrafts;
  useEffect(() => {
    if (!unsaved) return;
    const onUnload = (e: BeforeUnloadEvent) => { if (!unsavedRef.current) return; e.preventDefault(); e.returnValue = ''; };
    const onClick = (e: MouseEvent) => {
      if (!unsavedRef.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.hasAttribute('download') || a.getAttribute('href')?.startsWith('/api/')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin === window.location.origin && url.pathname === window.location.pathname) return;
      if (!window.confirm(LEAVE_SUBTITLES)) { e.preventDefault(); e.stopPropagation(); } else dropRef.current();
    };
    const mark = () => window.history.pushState({ ...(window.history.state ?? {}), prismSubtitleDraft: true }, '');
    if (!window.history.state?.prismSubtitleDraft) mark();
    const onPop = () => {
      if (!unsavedRef.current) return;
      if (!window.confirm(LEAVE_SUBTITLES)) { mark(); return; }
      dropRef.current();
      window.history.back();
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onPop);
      // Saved or discarded while still here: the guard's history entry goes, so Back leaves at the first press.
      if (!unsavedRef.current && window.history.state?.prismSubtitleDraft) window.history.back();
    };
  }, [unsaved]);

  // Opened from a search moment (?t=seconds): jump there once the player is ready.
  const [params] = useSearchParams();
  const startAt = Number(params.get('t'));
  // A subtitle moment opens the Subtitles tab (?tab=subtitles) at the cue.
  const wantTab = params.get('tab');
  useEffect(() => {
    if (wantTab === 'subtitles') setTab('subtitles');
    // The QC tab (1.0.0-rc.8): from a failed-run email, a workflow run or a QC badge.
    if (wantTab === 'qc') setTab('qc');
    // The Masters tab (1.2.0); the Versions and Processing tabs (1.2.3: links from what uses a version).
    if (wantTab === 'masters' || wantTab === 'versions' || wantTab === 'processing') setTab(wantTab);
    // The Sharing tab (1.6.3: from Administration → Shared links).
    if (wantTab === 'sharing') setTab('sharing');
    // 1.8.0: the Rights tab (from the digest email and the editor's Rights tab).
    if (wantTab === 'rights') setTab('rights');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantTab, id]);
  const proxyReady = Boolean(q.data?.media.proxyUrl);
  useEffect(() => {
    if (!proxyReady || !Number.isFinite(startAt) || startAt <= 0) return;
    const t = window.setTimeout(() => playerRef.current?.seek(startAt), 300);
    return () => window.clearTimeout(t);
  }, [proxyReady, startAt]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || isTyping(e, 'input, textarea, select, [role=dialog]')) return;
      if (unsavedRef.current) { if (!window.confirm(LEAVE_SUBTITLES)) return; dropRef.current(); }
      navigate('/library');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // On a placeholder's page a dropped file goes into that asset.
  const placeholderTarget = q.data?.status === 'placeholder' && !q.data.deleted ? { id: q.data.id, assetCode: q.data.assetCode, title: q.data.title } : null;
  const targetId = placeholderTarget?.id;
  useEffect(() => {
    if (!placeholderTarget) return;
    uploader.defaultTarget = placeholderTarget;
    return () => { if (uploader.defaultTarget?.id === placeholderTarget.id) uploader.defaultTarget = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId]);

  if (q.isLoading) {
    return <div className="empty"><Loader2 className="spin" color="var(--accent)" /></div>;
  }
  if (q.isError || !q.data) {
    const notFound = (q.error as ApiError | undefined)?.status === 404;
    return (
      <EmptyState icon={<AlertOctagon size={24} />} title={notFound ? 'Asset not found' : 'This asset could not be loaded'}>
        <p>{notFound ? 'It may have been removed, or it belongs to a workspace you cannot access.' : (q.error as Error)?.message}</p>
        <Link className="btn" to="/library">Back to library</Link>
      </EmptyState>
    );
  }
  const asset = q.data;
  const latestRun = asset.runs[0];
  const programme = programmeMarks(schema.data?.definition, asset.metadata);
  const programmeFields = { in: schema.data?.definition.fields.find((f) => f.role === 'programme_in'), out: schema.data?.definition.fields.find((f) => f.role === 'programme_out') };
  const canSetProgramme = Boolean(me.data?.permissions.includes('asset.edit_metadata') && !asset.deleted && (programmeFields.in || programmeFields.out));
  async function setProgramme(marks: { in: string | null; out: string | null }) {
    const values: Record<string, unknown> = {};
    if (programmeFields.in && marks.in) values[programmeFields.in.id] = marks.in;
    if (programmeFields.out && marks.out) values[programmeFields.out.id] = marks.out;
    try {
      await saveMetadata.mutateAsync({ revision: asset.metadataRevision, values });
      toast(`Programme ${marks.in ?? programme.in ?? 'start'} – ${marks.out ?? programme.out ?? 'end'} saved`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  // 1.9.0: while the Subtitles tab is open (and the details pane shown), the player shows the draft of the track
  // being edited, its cues on the timeline and the subtitle strip under it.
  const editTrack = (subtitles.data?.items ?? []).find((t) => t.id === selectedTrack(asset.id)) ?? subtitles.data?.items[0] ?? null;
  const editDraftNow = editTrack ? getDraft(asset.id, editTrack.id) : null;
  const canEditSubtitles = Boolean(me.data?.permissions.includes('asset.edit_metadata')) && !asset.deleted;
  const subtitleEditing: SubtitleEditing | null = tab === 'subtitles' && !panes.isHidden('details') && editTrack && editDraftNow ? {
    trackId: editTrack.id,
    cues: editDraftNow.cues,
    style: editDraftNow.style,
    selectedIds: getCueSelection(asset.id, editTrack.id).ids,
    rate,
    startFrames,
    readOnly: !canEditSubtitles,
    onSelect: (cueId, { extend }) => {
      const next = clickSelection(getCueSelection(asset.id, editTrack.id), cueId, extend);
      setCueSelection(asset.id, editTrack.id, next);
      requestAnimationFrame(() => document.querySelector(`[data-testid="subtitles-tab"] [data-cue="${cueId}"]`)?.scrollIntoView({ block: 'nearest' }));
    },
    onCues: (next) => { const d = getDraft(asset.id, editTrack.id); if (d && canEditSubtitles) setDraft(editDraft(d, { cues: next })); },
    onStyle: (ids, patch) => { const d = getDraft(asset.id, editTrack.id); if (d && canEditSubtitles) setDraft(editDraft(d, { cues: applyCueStyle(d.cues, ids, patch) })); },
  } : null;

  paneRender.current = {
    player: () => (
            <div className="player-column">
              {asset.status === 'placeholder'
                ? <PlaceholderUpload asset={asset} />
                : asset.mediaKind === 'graphic' && asset.graphic
                ? <GraphicAsset asset={asset} />
                : asset.live && !asset.media.proxyUrl
                ? <LivePlayer live={asset.live} onTimeChange={setPlayerTime} onPlayingChange={setPlayerPlaying} />
                : asset.media.proxyUrl && asset.mediaKind === 'image'
                ? <ImageViewer asset={asset} />
                : asset.media.proxyUrl
                ? <Player ref={playerRef} asset={asset} onTimeChange={setPlayerTime} onPlayingChange={setPlayerPlaying} subtitleTracks={subtitles.data?.items ?? []} subtitleEditing={subtitleEditing} programme={programme} onSetProgramme={canSetProgramme ? (m) => void setProgramme(m) : undefined} />
                : <NoProxy asset={asset} run={latestRun} />}
            </div>
    ),
    details: () => (
            <section className="pane raised" aria-label="Asset details">
              {/* The tabs scroll with an overflow cue; the pane buttons have their own room beside them (rc.9 fixes). */}
              <div className="tabs tab-row">
                <OverflowTabs className="tab-strip" wrapClassName="tab-strip-wrap" role="tablist" label="Asset details" activeKey={tab} testId="asset-tabs">
                {([
                  ['metadata', 'Metadata'], ['rights', 'Rights'], ['related', 'Related'], ['log', 'Log'], ['subtitles', 'Subtitles'], ['attachments', 'Files'], ['sharing', 'Sharing'], ['technical', 'Technical'], ['masters', 'Masters'],
                  ['versions', 'Versions'], ['qc', 'QC'], ['storage', 'Storage'], ['processing', 'Processing'], ['activity', 'Activity'],
                ] as Array<[Tab, string]>).filter(([k]) => (asset.status !== 'placeholder' && asset.mediaKind !== 'graphic') || !['log', 'subtitles', 'technical', 'masters', 'qc'].includes(k))
                  // Masters (1.2.0) are video or audio files of the same content.
                  .filter(([k]) => k !== 'masters' || asset.mediaKind === 'video' || asset.mediaKind === 'audio')
                  .filter(([k]) => k !== 'rights' || Boolean(asset.rights))
                  // Versions (1.2.3): with more than one, or for people who may delete old versions (an asset with media).
                  .filter(([k]) => k !== 'versions' || (asset.status !== 'placeholder' && ((asset.versionCount ?? 1) > 1 || Boolean(me.data?.permissions.includes('asset.delete_versions'))))).map(([k, label]) => (
                  <button key={k} role="tab" className="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}>
                    {label}
                    {k === 'storage' && <span className="count">{asset.representations.length}</span>}
                    {k === 'masters' && (asset.masters?.length ?? 0) > 1 && <span className="count" data-testid="masters-count">{asset.masters!.length}</span>}
                    {k === 'versions' && (asset.versionCount ?? 1) > 1 && <span className="count" data-testid="versions-count">{asset.versionCount}</span>}
                    {k === 'processing' && latestRun && latestRun.status === 'running' && <Loader2 size={12} className="spin" />}
                    {k === 'qc' && asset.qc && <QcBadge status={asset.qc.effectiveStatus} resolved={asset.qc.resolved} compact />}
                    {k === 'processing' && (asset.status === 'failed' || asset.status === 'partial') && <AlertOctagon size={12} color={asset.status === 'failed' ? 'var(--danger)' : 'var(--warning)'} />}
                  </button>
                ))}
                </OverflowTabs>
                <PaneControls />
              </div>
              <div className="tab-body" role="tabpanel">
                {tab === 'metadata' && <MetadataTab asset={asset} />}
                {/* A remembered Rights tab while rights are off shows the Metadata tab. */}
                {tab === 'rights' && (asset.rights ? <RightsTab asset={asset} /> : <MetadataTab asset={asset} />)}
                {tab === 'related' && <RelationsTab asset={asset} />}
                {tab === 'log' && <LogTab asset={asset} currentSeconds={playerTime} playing={playerPlaying} onSeek={(s) => playerRef.current?.seek(s)} range={() => playerRef.current?.range()} />}
                {tab === 'subtitles' && <SubtitlesTab asset={asset} currentSeconds={playerTime} playing={playerPlaying} player={subtitlePlayer} rate={rate} startFrames={startFrames} />}
                {tab === 'attachments' && <AttachmentsTab asset={asset} />}
                {tab === 'sharing' && <SharingTab asset={asset} currentSeconds={playerTime} onSeek={(s) => playerRef.current?.seek(s)} />}
                {tab === 'technical' && <TechnicalTab asset={asset} />}
                {tab === 'masters' && <MastersTab asset={asset} />}
                {tab === 'versions' && <VersionsTab asset={asset} />}
                {tab === 'qc' && <QcTab asset={asset} onSeek={(s) => playerRef.current?.seek(s)} />}
                {tab === 'storage' && <StorageTab asset={asset} />}
                {tab === 'processing' && <ProcessingTab asset={asset} />}
                {tab === 'activity' && <ActivityTab id={asset.id} />}
              </div>
            </section>
    ),
  };

  return (
    <>
      <header className="asset-header">
        <Link to="/library" className="btn ghost icon" aria-label="Back to library" title="Back (Esc)"><ArrowLeft size={17} /></Link>
        <h1 data-testid="asset-title">{asset.title}</h1>
        <AssetCode code={asset.assetCode} />
        {/* 1.4.0 (G2): "Series › Season 1 – Murder House · Episode 3". */}
        <AssetGroupsBreadcrumb asset={asset} />
        <CategoryPicker asset={asset} />
        <AssetStatus status={asset.status} availability={asset.availability} label={asset.status === 'processing' && asset.progress ? asset.progress.label : undefined} />
        {asset.qc && <QcBadge status={asset.qc.effectiveStatus} resolved={asset.qc.resolved} onClick={() => setTab('qc')} />}
        <RightsPill rights={asset.rights} onClick={() => setTab('rights')} />

        {asset.metadataIncomplete && !asset.deleted && (
          <button type="button" className="pill warning" data-testid="incomplete-pill" title="A needed field is empty. Open the Metadata tab to see which."
            onClick={() => setTab('metadata')}><AlertTriangle size={11} style={{ marginRight: 4 }} /> Incomplete metadata</button>
        )}
        {asset.duplicateOf && <Link to={`/assets/${asset.duplicateOf}`} className="pill" title="Identical content was already in the library">Duplicate of existing asset</Link>}
        {/* The actions never fall outside the window: those that do not fit move into More (…), least important first (rc.9 fixes). */}
        <OverflowBar name="asset" className="asset-actions" label="Asset actions" actions={[
          { key: 'access', priority: 20, node: <AssetAccess asset={asset} /> },
          { key: 'collection', priority: 40, node: !asset.deleted && <AddToCollectionButton subject="asset" id={asset.id} name={asset.title} /> },
          { key: 'assign', priority: 30, node: !asset.deleted && <AssignButton subject={{ type: 'asset', id: asset.id, name: asset.title }} /> },
          // Assign for delivery… (1.3.0, portal.assign): a placeholder, through the media portal.
          { key: 'delivery', priority: 32, node: <AssignForDeliveryButton asset={asset} /> },
          { key: 'share', priority: 60, node: <ShareButton asset={asset} /> },
          // Upload… (1.2.2): a new version of this asset, or a new master of this version.
          { key: 'upload', priority: 65, node: <AssetUploadButton asset={asset} onOpenTab={setTab} /> },
          { key: 'qc', priority: 50, node: !asset.deleted && asset.status !== 'placeholder' && asset.mediaKind !== 'graphic' && <RunQcButton assetId={asset.id} title={asset.title} onOpenTab={() => setTab('qc')} /> },
          // Detect shots… (1.5.0, S2): processing.retry; low priority, so it is in More.
          { key: 'shots', priority: 12, node: !asset.deleted && <DetectShotsButton asset={asset} /> },
          // Regenerate proxy (1.6.1): administrators, an asset that has a proxy; low priority, so it is in More.
          { key: 'regenerate', priority: 11, node: <RegenerateProxyButton asset={asset} /> },
          { key: 'workflow', priority: 80, node: !asset.deleted && <RunWorkflowButton subject="asset" id={asset.id} name={asset.title} /> },
          { key: 'graphic', priority: 85, node: !asset.deleted && (asset.mediaKind === 'graphic' || /\.zip$/i.test(asset.originalFilename ?? '')) && <EditGraphicButton assetId={asset.id} title={asset.title} /> },
          { key: 'export', priority: 90, node: !asset.deleted && me.data?.permissions.includes('export.create') && asset.versionId && <AssetExportButton asset={asset} programme={programme} marked={() => playerRef.current?.range()} /> },
          { key: 'panes', priority: 10, node: <PanesMenu layout={panes} /> },
          { key: 'delete', priority: 15, node: <DeleteAssetButton asset={asset} onDeleted={() => navigate('/library')} /> },
          { key: 'version', priority: 5, node: asset.versionNumber != null && <span className="muted asset-version" title={`Version ${asset.versionNumber} · ${asset.originalFilename ?? ''}`}>v{asset.versionNumber} · {asset.originalFilename}</span> },
          { key: 'original', priority: 70, node: asset.media.originalUrl ? (
            <a className="btn sm" href={asset.media.originalUrl} download={asset.originalFilename ?? undefined}><Download size={14} /> Original</a>
          ) : asset.availability === 'archived' || asset.availability === 'restoring' ? (
            <span className="pill" data-testid="original-archived" title="The original is in an archive. Restore it to download it.">
              {asset.availability === 'archived' ? 'Original archived' : 'Restoring original…'}
            </span>
          ) : asset.representations.find((r) => r.kind === 'original')?.locations.some((l) => l.storageKind === 'agent') ? (
            <span className="pill" title="The original is managed in place on site. Request a transfer from the Storage tab to copy it centrally.">
              <Server size={12} style={{ marginRight: 5 }} /> Original on site {asset.representations.find((r) => r.kind === 'original')!.locations[0]!.site}
            </span>
          ) : null },
        ]} />
      </header>
      {asset.deleted && <div style={{ padding: '10px 18px 0' }}><DeletedBanner asset={asset} onPurged={() => navigate('/library')} /></div>}
      {!asset.deleted && (asset.availability === 'archived' || asset.availability === 'restoring') && (
        <div style={{ padding: '10px 18px 0' }} data-testid="asset-archive-banner"><RestoreFromArchive assetId={asset.id} title={asset.title} availability={asset.availability} /></div>
      )}
      <div className="asset-layout">
        <PaneGroup layout={panes} row="main" />
      </div>
    </>
  );
}

/** The player's place for an asset without media: choose or drop a file to upload into this asset. */
function PlaceholderUpload({ asset }: { asset: AssetDetail }) {
  const me = useMe().data!;
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const canUpload = me.permissions.includes('asset.upload') && !asset.deleted;
  const upload = (files: File[]) => {
    if (!files.length) return;
    uploader.add(files.slice(0, 1), { placeholder: { id: asset.id, assetCode: asset.assetCode, title: asset.title } });
    toast(`Uploading into ${asset.assetCode}`);
  };
  const mine = useUploads().find((u) => u.placeholder?.id === asset.id && !['done', 'cancelled', 'error', 'duplicate'].includes(u.state));
  return (
    <div className="player-stage" style={{ flex: 1 }}>
      <div className={`placeholder-drop ${over ? 'over' : ''}`} data-testid="placeholder-panel"
        onDragOver={(e) => { if (canUpload) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { setOver(false); if (canUpload && e.dataTransfer.files.length) { e.preventDefault(); e.stopPropagation(); upload(Array.from(e.dataTransfer.files)); } }}>
        <div className="empty-icon"><FileQuestion size={24} /></div>
        <h2 style={{ margin: 0, fontSize: 17 }}>No media yet</h2>
        <p className="muted" style={{ margin: 0, maxWidth: 420 }}>
          This placeholder has its asset ID and metadata. {canUpload ? 'Upload a file into it, or drop one anywhere on this page.' : 'Your role cannot upload media into it.'}
        </p>
        {canUpload && (
          <>
            <input ref={input} type="file" hidden accept={uploadAccept()} data-testid="placeholder-file"
              onChange={(e) => { upload(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
            <button className="btn primary" disabled={Boolean(mine)} onClick={() => input.current?.click()} data-testid="placeholder-upload">
              {mine ? <Loader2 size={14} className="spin" /> : <Upload size={14} />} {mine ? 'Uploading…' : 'Upload media'}
            </button>
          </>
        )}
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>A watch folder can fill it too: name {asset.assetCode} in the sidecar.</p>
        <PlaceholderDelivery asset={asset} />
      </div>
    </div>
  );
}

function NoProxy({ asset, run }: { asset: AssetDetail; run: ProcessingRunDTO | undefined }) {
  const failed = asset.status === 'failed';
  const me = useMe().data!;
  const process = useAssetAction('process');
  if (asset.status === 'ready' || (asset.status === 'partial' && !asset.media.proxyUrl && run?.status !== 'running')) {
    return (
      <div className="player-stage" style={{ flex: 1 }}>
        <div className="empty-inner" style={{ padding: 24 }} data-testid="no-proxy">
          <div className="empty-icon"><CircleDashed size={24} /></div>
          <h2 style={{ margin: 0, fontSize: 17 }}>No proxy for this asset</h2>
          <p className="muted" style={{ margin: 0 }}>It was catalogued without a browser proxy, poster or scrub images.</p>
          {me.permissions.includes('processing.retry') && !asset.deleted && (
            <div className="actions">
              <button className="btn sm" disabled={process.isPending} data-testid="generate-proxy"
                onClick={() => process.mutate(asset.id, { onSuccess: () => toast('Generating a proxy'), onError: (e) => toast(errorMessage(e), 'error') })}>
                <RotateCcw size={13} /> Generate proxy
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="player-stage" style={{ flex: 1 }}>
      <div className="empty-inner" style={{ padding: 24 }}>
        {failed ? (
          <>
            <div className="empty-icon" style={{ background: 'var(--danger-soft)', color: 'var(--danger)', borderColor: 'rgba(242,98,111,.35)' }}><AlertOctagon size={24} /></div>
            <h2 style={{ margin: 0, fontSize: 17 }}>{asset.error?.code === 'INVALID_MEDIA' ? 'This file could not be read as media' : 'Processing failed'}</h2>
            <p className="muted" style={{ margin: 0 }} data-testid="failure-message">{asset.error?.message}</p>
            {asset.error?.hint && <p className="faint" style={{ margin: 0 }}>{asset.error.hint}</p>}
            <RetryBar asset={asset} />
          </>
        ) : (
          <>
            <div className="empty-icon"><Loader2 size={24} className="spin" /></div>
            <h2 style={{ margin: 0, fontSize: 17 }}>Preparing a playable proxy</h2>
            <p className="muted" style={{ margin: 0 }}>{asset.progress?.label ?? 'Queued'}{asset.progress?.percent != null ? ` · ${Math.round(asset.progress.percent)}%` : ''}</p>
            {run && <div style={{ width: 360, maxWidth: '80vw', textAlign: 'left', marginTop: 8 }}><Steps run={run} compact /></div>}
          </>
        )}
      </div>
    </div>
  );
}
